import { Component, EventEmitter, Input, Optional, ViewContainerRef } from '@angular/core';
import type { SafeResourceUrl } from '@angular/platform-browser';

import { PushPipe } from '@ngrx/component';

import { I18nService } from '@core/i18n-legacy';
import type { DialogComponentI, IconName } from '@core/user-interface-legacy';
import {
  buildDialogConfig,
  DialogBodyComponent,
  DialogCloseComponent,
  DialogRef,
  DialogWidth,
  HeadingComponent,
} from '@core/user-interface-legacy';

import type { WalkthroughComponentProperties } from '../../types';
import { WalkthroughDialogFooterComponent } from './walkthrough-dialog-footer/walkthrough-dialog-footer.component';

interface WalkthroughDialogData {
  ctaText: string;
  ctaHandler: () => void;
  ofText: string;
  heading: string;
  description: string;
  imageUrl: string;
  dialogHelpArticle: string;
  learnMoreText: string;
  displayCtaOnly: boolean;
  showCloseButton: boolean;
}

@Component({
  selector: 'rt-walkthrough-dialog',
  imports: [
    DialogBodyComponent,
    DialogCloseComponent,
    WalkthroughDialogFooterComponent,
    HeadingComponent,
    PushPipe,
  ],
  templateUrl: './walkthrough-dialog.component.html',
  styleUrl: './walkthrough-dialog.component.less',
})
export class WalkthroughDialogComponent
  implements WalkthroughComponentProperties, DialogComponentI<WalkthroughDialogData, void>
{
  @Input() public videoUrl?: SafeResourceUrl = undefined;
  @Input() public imageUrl?: string = undefined;

  @Input() public currentStep: number;
  @Input() public stepsTotal: number;

  @Input() public heading: string;
  @Input() public description: string;

  @Input() public ctaText: string;
  @Input() public ctaIcon?: IconName = undefined;
  @Input() public ctaHandler?: () => void;

  @Input() public ofText: string = this.i18n.translate('app.common.of');

  @Input() public closeText: string = this.i18n.translate('app.walkthrough.dialog.back');
  @Input() public closeIcon: IconName | undefined = 'chevron-left';

  @Input() public displayCtaOnly: boolean = false;
  @Input() public showCloseButton: boolean = false;

  @Input() public dialogHelpArticle: string;
  @Input() public learnMoreText: string;

  public cancel = new EventEmitter<void>();
  public finish = new EventEmitter<void>();
  public next = new EventEmitter<void>();

  public dialogConfig = buildDialogConfig({
    width: DialogWidth.Medium,
    viewContainerRef: this.viewRef,
  });

  constructor(
    private i18n: I18nService,
    private viewRef: ViewContainerRef,
    @Optional() private dialogRef: DialogRef,
  ) {}

  public setDialogData(data: WalkthroughDialogData): void {
    this.ctaText = data.ctaText;
    this.ofText = data.ofText;
    this.heading = data.heading;
    this.description = data.description;
    this.imageUrl = data.imageUrl;
    this.displayCtaOnly = data.displayCtaOnly;
    this.showCloseButton = data.showCloseButton;
    this.dialogHelpArticle = data.dialogHelpArticle;
    this.learnMoreText = data.learnMoreText;
    this.ctaHandler = data.ctaHandler;
  }

  public close() {
    this.cancel.emit();
  }

  public handleCtaClicked(): void {
    if (!!this.ctaHandler) {
      this.ctaHandler();
    }
    this.next.emit();
  }
}
